import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { AutomationExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { ReplyModal } from '../../modals/reply';

export type Payload = {
  templateId: string;
  templateName: string;
  nextNodeId: string;
};

type ReplyNodeAttributes = GenericNodeAttributes<Payload, AutomationExtraData>;
type ReplyNodeProps = GenericNodeProps<Payload, AutomationExtraData>;

const translatePayload: ReplyNodeAttributes['translatePayload'] = (node) => {
  const { nextNodeId } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: nextNodeId || uuid(),
    },
  ];
};

const updateConnection: ReplyNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, nextNodeId: connectionId };

  return { ...node, payload };
};

export const ReplyNodeAttributes: ReplyNodeAttributes = {
  iconName: 'reply',
  label: 'Auto-reply',
  helpText: 'Send an automatic reply based on a custom template.',
  color: hexStringToNumber(flowNodeColor.blue),
  translatePayload,
  updateConnection,
  NodeContent: ReplyNode,
  ActionModal: ReplyModal,
};

export function ReplyNode({ node }: ReplyNodeProps): JSX.Element {
  const { payload } = node;
  const { templateName } = payload;

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="normal" text="Template" />
            <Text weight="semibold" text={templateName} />
          </TextEllipsis>
        ),
        [templateName],
      )}
    </TextContent>
  );
}
